import { useClientDataLazyQuery } from "@qw/graphql";
import { KnownClientData } from "@qw/qw-common";
import { Button, HoverCard, HoverCardContent, HoverCardTrigger, Icon } from "@qw/ui";
import type { FC } from "react";
import type { InboxInsured } from "@/providers/use-inbox-insured";

// The physical address only. The mailing address is the same shape under its own
// prefix, but a card that has to label two addresses is a form rather than a
// glance — and physical is the one a broker checks a submission against.
const PHYSICAL_ADDRESS = KnownClientData.PhysicalAddress;
const PHYSICAL_ADDRESS_KEYS = [
  PHYSICAL_ADDRESS.AddressLine1,
  PHYSICAL_ADDRESS.AddressLine2,
  PHYSICAL_ADDRESS.City,
  PHYSICAL_ADDRESS.State,
  PHYSICAL_ADDRESS.Zip,
];

/**
 * Street lines first, then the city line, dropping whatever is missing. Close to
 * one in five active insureds has no physical address and plenty of the rest are
 * partial, so an empty result means no address block rather than a lone comma.
 */
const getAddressLines = (clientDataByKey: Map<string, string>) => {
  const stateAndZip = [clientDataByKey.get(PHYSICAL_ADDRESS.State), clientDataByKey.get(PHYSICAL_ADDRESS.Zip)]
    .filter(Boolean)
    .join(" ");
  const cityLine = [clientDataByKey.get(PHYSICAL_ADDRESS.City), stateAndZip].filter(Boolean).join(", ");

  return [
    clientDataByKey.get(PHYSICAL_ADDRESS.AddressLine1),
    clientDataByKey.get(PHYSICAL_ADDRESS.AddressLine2),
    cityLine,
  ].filter((line): line is string => Boolean(line));
};

/**
 * The rest of the insured, behind an info button in the rail's action row.
 *
 * Merged mode gives the broker one column for the insured, so the header has room
 * for a name and nothing else — dba, state, description and address have nowhere
 * to sit. A hover card is the right primitive because that detail is
 * supplemental: all of it is one click away in Brokerage, so revealing it on
 * hover withholds nothing.
 *
 * The trigger is a button rather than the name itself. The name is the one thing
 * the row always shows, so hanging the card off it made the reveal invisible;
 * a button beside the other three says the detail is there, and being a real
 * focusable control it also carries an accessible name.
 *
 * `dba`, `description` and `primaryState` ride the per-insured `InboxInsured`
 * query. The address does not — it is `ClientData`, not a column on `Insured` —
 * so it is fetched on the first open and not before.
 *
 * `enabled` is off where this would render inside another hover card
 * (`MiniAppToggle`). Nested cards are portalled out of their parent's content, so
 * moving the pointer into the inner one leaves the outer one and closes both.
 */
export const InsuredHoverCard: FC<{ enabled?: boolean; insured: InboxInsured }> = ({ enabled = true, insured }) => {
  const [loadClientData, { data }] = useClientDataLazyQuery();
  // Rows are taken by owner, not on trust. Nothing keys this component by
  // insured, so the hook keeps the previous insured's rows across a switch — and
  // since the fetch only fires on open, that stale set is exactly what is in hand
  // the first time the next insured's card renders.
  const clientDataByKey = new Map(
    (data?.clientData ?? []).filter(({ insuredId }) => insuredId === insured.id).map(({ key, value }) => [key, value])
  );
  const addressLines = getAddressLines(clientDataByKey);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      return;
    }

    // Apollo serves every open after the first from the cache, so this stays one
    // request per insured however often the pointer crosses the button.
    loadClientData({ variables: { input: { insuredId: insured.id, keys: PHYSICAL_ADDRESS_KEYS } } });
  };

  if (!enabled) {
    return null;
  }

  return (
    <HoverCard openDelay={200} onOpenChange={handleOpenChange}>
      {/* No tooltip on this one: it would race the card for the same hover. */}
      <HoverCardTrigger asChild>
        <Button variant="outline" size="sm" display="icon" aria-label="Insured details">
          <Icon icon="info" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" side="bottom" sideOffset={8} className="flex w-72 flex-col gap-3 p-4 text-sm">
        <div className="flex items-baseline gap-2">
          <h4 className="min-w-0 flex-auto font-medium">{insured.name}</h4>
          {insured.primaryState && (
            <span className="flex-none text-muted-foreground text-xs">{insured.primaryState}</span>
          )}
        </div>
        {insured.dba && <p className="text-muted-foreground text-xs">dba {insured.dba}</p>}
        {insured.description && <p className="text-muted-foreground">{insured.description}</p>}
        {addressLines.length > 0 && (
          <address className="text-muted-foreground not-italic">
            {addressLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </address>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
